import { useMemo, useState } from "react";

import { formatDate } from "simplydo/core/dates";
import { Button, Icon, Message, Modal, Table } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import type { CompanyMessage } from "components/innointel/types";
import { UserChip } from "components/lib/Chips";

import { InfoBoxWidget, InfoBoxWidgetTitleArea } from "../Styles";

type CompanyMessagesProps = { messages: CompanyMessage[] };

const CompanyMessages = ({ messages }: CompanyMessagesProps) => {
  const [selectedMessage, setSelectedMessage] = useState<CompanyMessage | null>(
    null,
  );
  const allMessagesWithLogs = useMemo(
    () => messages.filter((message) => !!message.emailLogs?.length),
    [messages],
  );

  const user = useAppSelector((state) => state.user);
  const organisationId = user?.ownerOrganisation?._id;
  const canListUsers =
    user && util.hasPermission(user, "org.listUsers", organisationId);

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        closeOnDimmerClick
      >
        <Modal.Header>{selectedMessage?.subject}</Modal.Header>
        <Modal.Content>
          <p>{selectedMessage?.message}</p>
        </Modal.Content>
      </Modal>
      <InfoBoxWidget $noBox>
        <InfoBoxWidgetTitleArea>
          <Icon name="mail" />
          <div>
            <h3>Messages</h3>
            <span>{messages.length} total</span>
          </div>
        </InfoBoxWidgetTitleArea>
        <p>
          These are communications sent by you or someone working in your
          organisation to this company.
        </p>
        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
          {messages.length > 0 ?
            <Table compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Subject</Table.HeaderCell>
                  {canListUsers ?
                    <Table.HeaderCell>From</Table.HeaderCell>
                  : null}
                  <Table.HeaderCell>Reply-To address</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  {allMessagesWithLogs.length > 0 ?
                    <>
                      <Table.HeaderCell collapsing>Sent</Table.HeaderCell>
                      <Table.HeaderCell collapsing>Delivered</Table.HeaderCell>
                      <Table.HeaderCell collapsing>Opened</Table.HeaderCell>
                      <Table.HeaderCell collapsing>Bounced</Table.HeaderCell>
                    </>
                  : null}
                  <Table.HeaderCell></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {messages.map((message) => {
                  const messageLog = message.emailLogs?.[0];
                  return (
                    <Table.Row key={message._id}>
                      <Table.Cell>{message.subject}</Table.Cell>
                      {canListUsers ?
                        <Table.Cell collapsing>
                          {message.ownerUser ?
                            <UserChip user={message.ownerUser} />
                          : null}
                        </Table.Cell>
                      : null}
                      <Table.Cell collapsing>{message.replyTo}</Table.Cell>
                      <Table.Cell collapsing>
                        {formatDate(message.createdAt)}
                      </Table.Cell>
                      {allMessagesWithLogs.length > 0 ?
                        <>
                          <Table.Cell>
                            {messageLog?.sent ?
                              <Icon name="check" color="green" />
                            : null}
                          </Table.Cell>
                          <Table.Cell>
                            {messageLog?.events?.delivered ?
                              <Icon name="check" color="green" />
                            : null}
                          </Table.Cell>
                          <Table.Cell>
                            {messageLog?.events?.open ?
                              <Icon name="check" color="green" />
                            : null}
                          </Table.Cell>
                          <Table.Cell>
                            {messageLog?.events?.bounced ?
                              <Icon name="close" color="red" />
                            : null}
                          </Table.Cell>
                        </>
                      : null}

                      <Table.Cell collapsing>
                        <Button onClick={() => setSelectedMessage(message)}>
                          View message
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          : <Message>
              <p>No messages have been sent to this company yet.</p>
            </Message>
          }
        </div>
      </InfoBoxWidget>
    </>
  );
};

export default CompanyMessages;
